import { booleanAttribute, computed, Directive, DoCheck, inject, input, linkedSignal, output } from '@angular/core';
import { NG_VALIDATORS, NgControl, ValidationErrors } from '@angular/forms';
import type { ValidationError } from '@angular/forms/signals';
import { TranslateService } from '@ngx-translate/core';
import { errorsToText } from './error-to-text.helper';

/**
 * Base for shared V2 form controls built on the Signal Forms custom-control contract.
 * `[formField]`, `[formControl]`, `formControlName` and `ngModel` all bind directly to these
 * inputs and outputs, so no `ControlValueAccessor` is involved.
 *
 * Subclasses render `viewValue()`, write UI changes through `updateValue()` and call
 * `markAsTouched()` when the user leaves the control.
 */
@Directive()
export abstract class DefaultFormValueControl<T> implements DoCheck {
  protected readonly translateService = inject(TranslateService);
  // Read for `updateOn` and template validators, which Angular's custom-control binding both ignores.
  private readonly ngControl = inject(NgControl, { self: true, optional: true });
  private readonly templateValidators = inject(NG_VALIDATORS, { self: true, optional: true }) ?? [];
  private hasPendingChange = false;

  // A `value` input plus `valueChange` output instead of `model()`: a model only emits on change,
  // but consumers rely on every user selection reaching the form, even a repeated one.
  public readonly value = input<T>(undefined);
  public readonly valueChange = output<T>();
  public readonly errors = input<readonly ValidationError.WithOptionalFieldTree[]>([]);
  public readonly disabled = input(false, { transform: booleanAttribute });
  public readonly required = input(false, { transform: booleanAttribute });
  public readonly touched = input(false);
  public readonly touch = output<void>();

  /** The value shown in the UI. Runs ahead of `value` until the form writes it back, or until blur with `updateOn: 'blur'`. */
  public readonly viewValue = linkedSignal(() => this.toViewValue(this.value()));

  /** The bound errors keyed by kind, shaped like `AbstractControl.errors`. */
  public readonly validationErrors = computed<ValidationErrors | null>(() => {
    const errors = this.errors();
    if (!errors.length) return null;
    // Reactive Forms errors carry the original `ValidationErrors` value as `context`.
    return Object.fromEntries(errors.map(error => [error.kind, 'context' in error ? error.context : error]));
  });

  public readonly errorTooltip = computed(() => {
    const errors = this.validationErrors();
    if (!errors) return null;
    return errorsToText(errors)
      .map(error => this.translateService.instant(error))
      .join('\n\n');
  });

  // Angular's custom-control binding skips template validators (`required`, `maxlength`), unlike the ControlValueAccessor setup.
  public ngDoCheck(): void {
    const control = this.ngControl?.control;
    const validator = this.ngControl?.validator;
    if (!control || !validator || control.hasValidator(validator)) return;
    control.addValidators(validator);
    // The validators only read their inputs in their own ngOnChanges, which runs after this hook.
    this.templateValidators.forEach(
      v =>
        typeof v !== 'function' &&
        v.registerOnValidatorChange?.(() => control.updateValueAndValidity({ emitEvent: false }))
    );
  }

  /** Normalizes a value written by the form before it is shown. The form value itself is left as is. */
  protected toViewValue(value: T): T {
    return value;
  }

  protected updateValue(value: T): void {
    this.viewValue.set(value);
    this.hasPendingChange = this.updatesOnBlur();
    if (!this.hasPendingChange) this.valueChange.emit(value);
  }

  protected markAsTouched(): void {
    if (this.hasPendingChange) {
      this.hasPendingChange = false;
      this.valueChange.emit(this.viewValue());
    }
    this.touch.emit();
  }

  private updatesOnBlur(): boolean {
    return this.ngControl?.control?.updateOn === 'blur';
  }
}
